import { useState, useRef, useCallback, useEffect } from 'react'

/**
 * Custom hook for touch gestures: pinch-zoom, double-tap zoom, pan when zoomed, swipe left/right
 * 
 * @param {Object} options
 * @param {number} options.maxScale - Maximum zoom scale (default: 5)
 * @param {number} options.doubleTapScale - Scale on double tap (default: 2.5)
 * @param {number} options.swipeThreshold - Min px for swipe detection (default: 50)
 * @param {Function} options.onSwipeLeft - Callback when swiping left
 * @param {Function} options.onSwipeRight - Callback when swiping right
 * @param {boolean} options.enabled - Enable/disable gestures (default: true)
 * 
 * @returns {Object} { scale, offsetX, offsetY, isZoomed, resetZoom, containerRef, handlers }
 */
export const useTouchGestures = ({
    maxScale = 5,
    doubleTapScale = 2.5,
    swipeThreshold = 50,
    onSwipeLeft,
    onSwipeRight,
    enabled = true,
} = {}) => {
    // State for transform
    const [scale, setScale] = useState(1)
    const [offsetX, setOffsetX] = useState(0)
    const [offsetY, setOffsetY] = useState(0)
    
    // Track when element is mounted for useEffect
    const [elementMounted, setElementMounted] = useState(false)

    // Refs to avoid stale closures in event handlers
    const scaleRef = useRef(1)
    const touchStartXRef = useRef(0)
    const touchStartYRef = useRef(0)
    const touchStartDistRef = useRef(0)
    const touchStartScaleRef = useRef(1)
    const touchPanOffsetXRef = useRef(0)
    const touchPanOffsetYRef = useRef(0)
    const lastTapRef = useRef(0)
    const containerRef = useRef(null)

    const isZoomed = scale > 1

    // Reset zoom to default
    const resetZoom = useCallback(() => {
        setScale(1)
        scaleRef.current = 1
        setOffsetX(0)
        setOffsetY(0)
    }, [])

    // Calculate distance between two touch points
    const getTouchDistance = useCallback((touches) => {
        const dx = touches[0].clientX - touches[1].clientX
        const dy = touches[0].clientY - touches[1].clientY
        return Math.sqrt(dx * dx + dy * dy)
    }, [])

    // Touch start handler
    const onTouchStart = useCallback((e) => {
        if (!enabled) return

        if (e.touches.length === 2) {
            // Pinch start - record initial distance and scale
            touchStartDistRef.current = getTouchDistance(e.touches)
            touchStartScaleRef.current = scaleRef.current
            touchPanOffsetXRef.current = offsetX
            touchPanOffsetYRef.current = offsetY
        } else if (e.touches.length === 1) {
            // Single touch - record start position
            touchStartXRef.current = e.touches[0].clientX
            touchStartYRef.current = e.touches[0].clientY
            touchPanOffsetXRef.current = offsetX
            touchPanOffsetYRef.current = offsetY

            // Double-tap detection
            const now = Date.now()
            if (now - lastTapRef.current < 300) {
                // Double tap detected - toggle zoom
                if (scaleRef.current > 1) {
                    resetZoom()
                } else {
                    const newScale = doubleTapScale
                    setScale(newScale)
                    scaleRef.current = newScale
                }
            }
            lastTapRef.current = now
        }
    }, [enabled, offsetX, offsetY, doubleTapScale, getTouchDistance, resetZoom])

    // Touch move handler
    const onTouchMove = useCallback((e) => {
        if (!enabled) return

        if (e.touches.length === 2) {
            // Pinch zoom
            const newDist = getTouchDistance(e.touches)
            const newScale = Math.min(
                Math.max(touchStartScaleRef.current * (newDist / touchStartDistRef.current), 1),
                maxScale
            )
            setScale(newScale)
            scaleRef.current = newScale
        } else if (e.touches.length === 1 && scaleRef.current > 1) {
            // Pan when zoomed
            const dx = e.touches[0].clientX - touchStartXRef.current
            const dy = e.touches[0].clientY - touchStartYRef.current
            setOffsetX(touchPanOffsetXRef.current + dx)
            setOffsetY(touchPanOffsetYRef.current + dy)
        }
    }, [enabled, maxScale, getTouchDistance])

    // Touch end handler
    const onTouchEnd = useCallback((e) => {
        if (!enabled) return

        // Swipe navigation only when not zoomed
        if (scaleRef.current === 1 && e.changedTouches.length === 1) {
            const dx = e.changedTouches[0].clientX - touchStartXRef.current
            const dy = Math.abs(e.changedTouches[0].clientY - touchStartYRef.current)
            
            // Check if horizontal swipe is dominant
            if (Math.abs(dx) > swipeThreshold && Math.abs(dx) > dy) {
                if (dx < 0 && onSwipeLeft) {
                    onSwipeLeft()
                } else if (dx > 0 && onSwipeRight) {
                    onSwipeRight()
                }
            }
        }

        // Snap back if scale somehow went below 1
        if (scaleRef.current < 1) {
            resetZoom()
        }
    }, [enabled, swipeThreshold, onSwipeLeft, onSwipeRight, resetZoom])

    // Callback ref to track when element is mounted
    const setContainerRef = useCallback((node) => {
        containerRef.current = node
        setElementMounted(!!node)
    }, [])

    // Attach passive:false touchmove listener to prevent browser scroll during pinch/pan
    useEffect(() => {
        const el = containerRef.current
        if (!el || !enabled) return

        const preventScroll = (e) => {
            // Prevent default for multi-touch (pinch) or when zoomed (pan)
            if (e.touches.length > 1 || scaleRef.current > 1) {
                e.preventDefault()
            }
        }

        el.addEventListener('touchmove', preventScroll, { passive: false })
        return () => el.removeEventListener('touchmove', preventScroll)
    }, [enabled, elementMounted])

    // Handler props to spread on the container element
    const handlers = {
        onTouchStart,
        onTouchMove,
        onTouchEnd,
    }

    // Transform style for the content
    const transformStyle = {
        transform: `scale(${scale}) translate(${scale > 1 ? offsetX / scale : 0}px, ${scale > 1 ? offsetY / scale : 0}px)`,
        transformOrigin: 'center center',
        transition: scale === 1 ? 'transform 0.25s ease' : 'none',
        willChange: 'transform',
    }

    return {
        scale,
        offsetX,
        offsetY,
        isZoomed,
        resetZoom,
        containerRef: setContainerRef, // Return callback ref instead of ref object
        handlers,
        transformStyle,
    }
}
